import { useCallback, useEffect, useState } from 'react';

function resolveSpeechLang(i18nLanguage: string): string {
  const base = i18nLanguage.split('-')[0]?.toLowerCase() ?? 'en';
  if (base === 'de') return 'de-DE';
  if (base === 'en') return 'en-US';
  return i18nLanguage;
}

function isSpeechSynthesisSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * Browser Web Speech API — speak / stop a short text (e.g. annotation description).
 */
export function useSpeechSynthesis(text: string, i18nLanguage: string) {
  const [speaking, setSpeaking] = useState(false);
  const supported = isSpeechSynthesisSupported();

  useEffect(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [text, supported]);

  useEffect(() => {
    if (!supported) return;
    return () => {
      window.speechSynthesis.cancel();
    };
  }, [supported]);

  const toggle = useCallback(() => {
    if (!supported || !text.trim()) return;

    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text.trim());
    utterance.lang = resolveSpeechLang(i18nLanguage);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    setSpeaking(true);
  }, [supported, text, i18nLanguage, speaking]);

  const stop = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [supported]);

  return { supported, speaking, toggle, stop };
}
